import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, ScrollView, Text, View } from 'react-native';
import ModalTitle from '../ModalTitle/ModalTitle';
import AndroidScreenRow from './AndroidScreenRow';
import { makeStyles } from './AndroidScreenSelectionModal.styles';
import { useTheme } from '../../../hooks/useTheme';
import {
  AndroidDisplayInfo,
  getAllDisplays,
  getCurrentDisplayId,
  moveToDisplay,
  requestDrawOverlays,
} from '../../../utils/secondaryDisplay';

interface Props {
  onClose: () => void;
}

const AndroidScreenSelectionModal: React.FC<Props> = ({ onClose }) => {
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const [displays, setDisplays] = useState<AndroidDisplayInfo[]>([]);
  const [savedDisplayId, setSavedDisplayId] = useState<number>(-1);
  const [loading, setLoading] = useState(true);
  const [isMoving, setIsMoving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const [allDisplays, currentId] = await Promise.all([
          getAllDisplays(),
          getCurrentDisplayId(),
        ]);
        if (mounted) {
          setDisplays(allDisplays);
          setSavedDisplayId(currentId);
        }
      } catch (e) {
        if (mounted) {
          setError('Failed to load displays. Please try again.');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, []);

  const handleSelect = useCallback(
    async (displayId: number) => {
      if (isMoving) return;
      setIsMoving(true);
      setError(null);
      try {
        await moveToDisplay(displayId);
        onClose();
      } catch (e) {
        const nativeError = e as { message?: string; code?: string };
        if (nativeError?.code === 'OVERLAY_PERMISSION_REQUIRED') {
          setError(
            'Permission required: Go to Settings → Apps → CDS → Display over other apps and enable it, then try again.',
          );
          try {
            await requestDrawOverlays();
          } catch {
            // Settings screen failed to open — error message is already shown
          }
        } else {
          setError(
            nativeError?.message ?? 'Failed to move to selected display.',
          );
        }
        setIsMoving(false);
      }
    },
    [isMoving, onClose],
  );

  return (
    <View style={styles.container}>
      <ModalTitle title="Select Display" onDismiss={onClose} />
      <View style={styles.content}>
        <Text style={styles.description}>
          Choose the display you want to move this app to.
        </Text>

        {error && (
          <View style={styles.errorContainer}>
            <Text style={styles.errorText}>{error}</Text>
          </View>
        )}

        {loading ? (
          <View style={styles.emptyContainer}>
            <ActivityIndicator size="large" color={colors.deepPurple} />
          </View>
        ) : displays.length === 0 ? (
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>No displays found.</Text>
          </View>
        ) : (
          <ScrollView style={styles.list}>
            <View style={styles.rows}>
              {displays.map(display => (
                <AndroidScreenRow
                  key={display.displayId}
                  {...display}
                  isSaved={display.displayId === savedDisplayId}
                  isDisabled={isMoving}
                  onSelect={handleSelect}
                />
              ))}
            </View>
          </ScrollView>
        )}
      </View>
    </View>
  );
};

export default AndroidScreenSelectionModal;
